import { Container, Graphics, Sprite, Text, TextStyle, Texture } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import { HUD } from '@/config/hud';
import { WEAPONS } from '@/config/weapons';
import { weaponGlyphKey } from '@/config/weapon-visuals';
import { arrowGlyph, bagGlyph, sparkleGlyph, swapGlyph } from './hangar-icons';
import type { CrateDestination } from './shop-types';
import { scrapGems } from './weapon-labels';
import { chamferPanel, hexPath, UI_FONT } from './widgets';

const T = HUD.weapons.toast;

/**
 * Wordless pickup toast under the weapon strip (docs/09 wave 4): the
 * weapon's own glyph → where it went — a numbered socket, the bag (with
 * the "new" sparkle), a full bag (swap at the break) or +N gems. Built
 * once; a pickup only redraws it, the frame update just animates.
 */
export class PickupToast {
  readonly view = new Container();
  private readonly bg = new Graphics();
  private readonly icon = new Sprite(Texture.EMPTY);
  private readonly marks = new Graphics();
  private readonly gem = new Sprite(Texture.EMPTY);
  private readonly num: Text;
  private age = -1;

  constructor() {
    this.icon.anchor.set(0.5);
    this.icon.scale.set(T.glyphScale);
    this.icon.position.set(34, T.h / 2);
    this.gem.anchor.set(0.5);
    this.gem.scale.set(0.6);
    this.num = new Text({
      text: '',
      style: new TextStyle({ fontFamily: UI_FONT, fontSize: 18, fill: 0xffffff, letterSpacing: 1 }),
    });
    this.num.anchor.set(0.5);
    this.view.addChild(this.bg, this.icon, this.marks, this.gem, this.num);
    this.view.pivot.set(0, T.h / 2);
    this.view.y = T.y + T.h / 2;
    this.view.visible = false;
  }

  show(weaponId: string, dest: CrateDestination, socket: number): void {
    const def = WEAPONS[weaponId];
    if (!def) return;
    const cat = WEAPON_CATEGORY_COLOR[def.category];
    this.bg.clear();
    chamferPanel(this.bg, T.w, T.h, {
      fill: 0x14101f, fillAlpha: 0.9, stroke: dest === 'full' ? COLORS.dangerOrange : cat,
      strokeWidth: 3, notch: 10, plain: true,
    });
    this.icon.texture = getTexture(weaponGlyphKey(def.id));
    const m = this.marks.clear();
    arrowGlyph(m, 82, T.h / 2, 12, 0xe8eaf0);
    const dx = 132;
    const cy = T.h / 2;
    this.gem.visible = false;
    this.num.text = '';
    this.num.style.fill = 0x0f0c1a;
    this.num.position.set(dx, cy);
    if (dest === 'socket') {
      hexPath(m, dx, cy, 17).fill(cat);
      this.num.text = String(socket + 1);
    } else if (dest === 'bag') {
      bagGlyph(m, dx, cy + 2, 15, COLORS.playerCream);
      sparkleGlyph(m, dx + 17, cy - 15, 7, COLORS.playerYellow);
    } else if (dest === 'full') {
      bagGlyph(m, dx - 6, cy + 2, 13, COLORS.dangerOrange);
      swapGlyph(m, dx + 18, cy - 12, 8, COLORS.playerYellow);
    } else {
      this.gem.texture = getTexture('pickup/gem-yellow');
      this.gem.visible = true;
      this.gem.position.set(dx - 14, cy);
      this.num.text = `+${scrapGems(def.id)}`;
      this.num.style.fill = COLORS.gemYellow;
      this.num.position.set(dx + 12, cy);
    }
    this.age = 0;
    this.view.visible = true;
  }

  update(dt: number): void {
    if (this.age < 0) return;
    this.age += dt;
    if (this.age >= T.life) {
      this.age = -1;
      this.view.visible = false;
      return;
    }
    // Pop in (overshoot), hold, fade out
    const p = Math.min(1, this.age / T.pop);
    this.view.scale.set(p < 1 ? 0.6 + 0.55 * p : 1 + 0.15 * Math.max(0, 1 - (this.age - T.pop) / T.pop));
    const fadeAt = T.life - T.fade;
    this.view.alpha = this.age > fadeAt ? Math.max(0, (T.life - this.age) / T.fade) : 1;
  }
}
